'use client';

import { useState, useCallback, useRef, Suspense } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { OrbitControls, PerspectiveCamera, Html } from '@react-three/drei';
import { ArrowsOut, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import * as THREE from 'three';

// Element properties (colors and radii)
const ELEMENT_DATA: Record<string, { color: string; radius: number }> = {
  H: { color: '#FFFFFF', radius: 0.31 },
  C: { color: '#909090', radius: 0.77 },
  N: { color: '#3050F8', radius: 0.71 },
  O: { color: '#FF0D0D', radius: 0.66 },
  S: { color: '#FFFF30', radius: 1.05 },
  P: { color: '#FF8000', radius: 1.07 },
  F: { color: '#90E050', radius: 0.57 },
  Cl: { color: '#1FF01F', radius: 1.02 },
  Br: { color: '#A62929', radius: 1.20 },
  I: { color: '#940094', radius: 1.39 },
  Fe: { color: '#E06633', radius: 1.26 },
  Na: { color: '#AB5CF2', radius: 1.66 },
  K: { color: '#8F40D4', radius: 2.03 },
  Ca: { color: '#3DFF00', radius: 1.76 },
  Mg: { color: '#8AFF00', radius: 1.41 },
  default: { color: '#FF1493', radius: 0.8 },
};

interface Atom {
  element: string;
  x: number;
  y: number;
  z: number;
  label?: string;
}

interface Molecule3DProps {
  title: string;
  atoms: Atom[];
  bonds: [number, number][];
  style?: 'ball-and-stick' | 'space-filling' | 'stick';
}

// Atom sphere component
function AtomSphere({ atom, index, style }: { atom: Atom; index: number; style: string }) {
  const elementData = ELEMENT_DATA[atom.element] || ELEMENT_DATA.default;
  const radius = style === 'space-filling' 
    ? elementData.radius 
    : style === 'stick' 
      ? 0.15 
      : elementData.radius * 0.4;

  return (
    <mesh position={[atom.x, atom.y, atom.z]}>
      <sphereGeometry args={[radius, 32, 32]} />
      <meshStandardMaterial color={elementData.color} />
    </mesh>
  );
}

// Bond cylinder component
function Bond({ atom1, atom2, style }: { atom1: Atom; atom2: Atom; style: string }) {
  const start = new THREE.Vector3(atom1.x, atom1.y, atom1.z);
  const end = new THREE.Vector3(atom2.x, atom2.y, atom2.z);
  const mid = new THREE.Vector3().addVectors(start, end).multiplyScalar(0.5);
  const length = start.distanceTo(end);
  
  // Calculate rotation to align cylinder with bond
  const direction = new THREE.Vector3().subVectors(end, start).normalize();
  const quaternion = new THREE.Quaternion();
  quaternion.setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction);

  const radius = style === 'stick' ? 0.08 : 0.05;

  return (
    <mesh position={mid} quaternion={quaternion}>
      <cylinderGeometry args={[radius, radius, length, 16]} />
      <meshStandardMaterial color="#666666" />
    </mesh>
  );
}

// Auto-rotating group
function MoleculeGroup({ children, autoRotate }: { children: React.ReactNode; autoRotate: boolean }) {
  const groupRef = useRef<THREE.Group>(null);
  
  useFrame((_, delta) => {
    if (autoRotate && groupRef.current) {
      groupRef.current.rotation.y += delta * 0.3;
    }
  });

  return <group ref={groupRef}>{children}</group>;
}

// Main 3D scene
function MoleculeScene({ atoms, bonds, style = 'ball-and-stick' }: Omit<Molecule3DProps, 'title'>) {
  const [autoRotate, setAutoRotate] = useState(true);

  // Calculate center of mass to center the molecule
  const center = atoms.reduce(
    (acc, atom) => ({ x: acc.x + atom.x, y: acc.y + atom.y, z: acc.z + atom.z }),
    { x: 0, y: 0, z: 0 }
  );
  center.x /= atoms.length;
  center.y /= atoms.length;
  center.z /= atoms.length;

  // Offset atoms to center
  const centeredAtoms = atoms.map(atom => ({
    ...atom,
    x: atom.x - center.x,
    y: atom.y - center.y,
    z: atom.z - center.z,
  }));

  // Calculate camera distance based on molecule size
  const maxDist = Math.max(
    ...centeredAtoms.map(a => Math.sqrt(a.x ** 2 + a.y ** 2 + a.z ** 2))
  );
  const cameraDistance = Math.max(maxDist * 3, 5);

  return (
    <>
      <PerspectiveCamera makeDefault position={[0, 0, cameraDistance]} />
      <OrbitControls 
        enablePan={true} 
        enableZoom={true} 
        enableRotate={true}
        onStart={() => setAutoRotate(false)}
      />
      <ambientLight intensity={0.5} />
      <directionalLight position={[10, 10, 5]} intensity={1} />
      <directionalLight position={[-10, -10, -5]} intensity={0.3} />
      
      <MoleculeGroup autoRotate={autoRotate}>
        {/* Render atoms */}
        {centeredAtoms.map((atom, i) => (
          <AtomSphere key={i} atom={atom} index={i} style={style} />
        ))}
        
        {/* Render bonds */}
        {style !== 'space-filling' && bonds.map(([a1, a2], i) => (
          <Bond key={i} atom1={centeredAtoms[a1]} atom2={centeredAtoms[a2]} style={style} />
        ))}
      </MoleculeGroup>
    </>
  );
}

// Loading fallback
function LoadingFallback() {
  return (
    <Html center>
      <div className="text-muted-foreground text-sm">Loading 3D view...</div>
    </Html>
  );
}

export function Molecule3DRenderer({ title, atoms, bonds, style = 'ball-and-stick' }: Molecule3DProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);

  const toggleFullscreen = useCallback(() => {
    setIsFullscreen(prev => !prev);
  }, []);

  // Handle escape key
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && isFullscreen) {
      setIsFullscreen(false);
    }
  }, [isFullscreen]);

  const canvasContent = (
    <Canvas>
      <Suspense fallback={<LoadingFallback />}>
        <MoleculeScene atoms={atoms} bonds={bonds} style={style} />
      </Suspense>
    </Canvas>
  );

  return (
    <>
      {/* Normal view */}
      <div className="w-full border rounded-lg overflow-hidden bg-card group relative">
        <div className="flex items-center justify-between px-4 py-2 border-b bg-muted/30">
          <h3 className="font-medium text-sm">{title}</h3>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {atoms.length} atoms, {bonds.length} bonds
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleFullscreen}
              className="opacity-0 group-hover:opacity-100 transition-opacity"
            >
              <ArrowsOut weight="bold" className="size-4" />
              <span className="ml-1 text-xs">Fullscreen</span>
            </Button>
          </div>
        </div>
        
        <div className="w-full h-[400px] bg-gradient-to-b from-slate-900 to-slate-800">
          {canvasContent}
        </div>
        
        <div className="px-4 py-2 border-t bg-muted/30 text-xs text-muted-foreground">
          Drag to rotate • Scroll to zoom • Click to stop auto-rotation
        </div>
      </div>

      {/* Fullscreen overlay */}
      {isFullscreen && (
        <div 
          className="fixed inset-0 z-50 bg-slate-900"
          onKeyDown={(e) => e.key === 'Escape' && setIsFullscreen(false)}
          tabIndex={0}
        >
          <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-6 py-4 bg-slate-900/90 backdrop-blur-sm">
            <h2 className="font-semibold text-lg text-white">{title}</h2>
            <Button
              variant="outline"
              size="sm"
              onClick={toggleFullscreen}
              className="bg-slate-800 border-slate-700 text-white hover:bg-slate-700"
            >
              <X weight="bold" className="size-4 mr-1" />
              Close
              <span className="text-xs text-slate-400 ml-2">(Esc)</span>
            </Button>
          </div>
          
          <div className="absolute inset-0 top-16">
            {canvasContent}
          </div>
        </div>
      )}
    </>
  );
}

/**
 * Renders molecule3d output from a tool call
 */
export function Molecule3DToolOutput({ output }: { 
  output: { 
    success: boolean; 
    title?: string; 
    atoms?: Atom[];
    bonds?: [number, number][];
    style?: 'ball-and-stick' | 'space-filling' | 'stick';
    error?: string;
  } 
}) {
  if (!output.success) {
    return (
      <div className="p-4 border border-amber-500/50 rounded-lg bg-amber-500/10">
        <p className="text-sm font-medium text-amber-600 dark:text-amber-400">Molecule Error</p>
        <pre className="mt-2 text-xs text-muted-foreground whitespace-pre-wrap">{output.error}</pre>
      </div>
    );
  }

  if (!output.atoms || !output.bonds) {
    return null;
  }

  return (
    <Molecule3DRenderer 
      title={output.title || 'Molecule'} 
      atoms={output.atoms} 
      bonds={output.bonds}
      style={output.style}
    />
  );
}
